/**
 * Tipos de video permitidos para apartamentos
 */
export enum VideoType {
  TOUR = 'tour',           // Recorrido completo del apartamento
  REEL = 'reel',           // Video corto estilo Instagram/TikTok
  AMENITY = 'amenity',     // Video de amenidad específica (piscina, vista, etc)
  VIEW_360 = '360',        // Video 360° (futuro)
}

/**
 * Información de un video de apartamento
 */
export interface ApartmentVideo {
  type: VideoType;         // Tipo de video
  url: string;             // URL principal (HLS .m3u8 Cloudflare Stream)
  thumbnail: string;       // URL del thumbnail (frame extraído)
  duration: number;        // Duración en segundos
  title?: string;          // Título descriptivo (opcional)
  description?: string;    // Descripción corta (opcional)
  // SEO 2026-05-10: campos opcionales para VideoObject JSON-LD.
  // Backfill vía scripts/backfill-video-seo-metadata.ts.
  uploadDate?: string;     // ISO 8601 (de Cloudflare Stream `created`)
  contentUrl?: string;     // MP4 directo (Cloudflare downloads/default.mp4) — preferido sobre HLS para crawlers
  embedUrl?: string;       // iframe player Cloudflare Stream — fallback para crawlers que no parsean HLS/MP4
  streamUid?: string;      // UID Cloudflare Stream extraído de la URL HLS
}

export interface ReviewStats {
  total: number; // Total de reviews activas (todas)
  reviewsInPeriod: number; // Reviews en el período adaptativo seleccionado
  selectedPeriod: string; // Período usado (ej: "1 año", "7 meses", "reseñas")
  avgRating?: number; // Rating promedio del período adaptativo (ej: 4.6)
  platforms?: { // Conteo por plataforma (opcional, para mostrar chips)
    Airbnb?: number;
    'Booking.com'?: number;
    Booking?: number; // Alias alternativo
    Google?: number;
    WhatsApp?: number;
  };
}


/**
 * Imagen con metadata SEO para apartamento
 */
export interface SeoImage {
  url: string;
  category?: string;
  alt?: string;
  description?: string;
}

export interface Apartment {
  id: string; // ID único simple (ej: "1820") - usado para URLs y referencias internas
  publicName: string; // Nombre público atractivo (ej: "Urban Sky") - sin prefijo "Apartamento/Estudio"
  briefDescription: string; // Descripción experiencial breve (máx 400 chars) - captura la esencia del apartamento
  beds24RoomId: string; // RoomID de Beds24 - NO MODIFICAR (sincronización disponibilidad)
  tipo: 'studio' | 'alcoba' | '2 alcobas' | '3 alcobas' | 'penthouse';
  piso: number;
  description: string;
  longDescription: string;
  destacados: string[];
  capacity: Capacity;
  distribucionCamas: DistribucionCamas;
  pricing: Pricing;
  amenities: string[] | AmenitiesCategorized; // Soportar ambos formatos
  amenitiesParticulares?: string[]; // Amenidades únicas de este apartamento (override de edificio)
  vistas?: ('mar' | 'ciudad' | 'panoramica' | 'lago')[];
  estilo?: 'moderno' | 'colonial';
  location: Location;
  images: string[];
  seoImages?: SeoImage[]; // Imágenes con metadata SEO desde PostgreSQL
  thumbnail: string;
  featuredImage?: string;
  videos?: ApartmentVideo[]; // Videos alojados en Cloudinary (tours, reels, amenidades)
  videoLocal?: string; // DEPRECATED - Ruta al video local optimizado (ej: /apartments/1820/video-tour.mp4)
  tiktokVideos?: string[]; // URLs externas de TikTok (para link "Ver en TikTok", NO para embed)
  instagramProfile?: string; // Handle de Instagram (ej: "apartamento.ocean.sound")
  enlaceWhatsApp?: string;
  airbnbUrl?: string; // URL del listado en Airbnb
  bookingUrl?: string; // URL del listado en Booking.com
  bookingHighlight?: 'favorito' | 'regular'; // Controla el texto del módulo de reservas destacadas
  reviews?: Review[]; // Comentarios reales de huéspedes
  reviewStats?: ReviewStats; // Estadísticas totales de reviews por plataforma
  featured: boolean;
  active: boolean;
  buildingId?: string; // ID del edificio al que pertenece
  companyId: number; // ID de la company propietaria (1=Pacartagena, 2=Luis del Mar, 3=ABBA Tours, 4=Miroslava). REQUERIDO: sin este campo, el sistema de markup falla silenciosamente y se pierde revenue de socios.
  companyName?: string; // Nombre de la company (de join a tabla companies)
  updatedAt?: string | Date; // Última actualización en BD (apartments.updated_at). Opcional porque el fallback (JSONs) no la trae.
}

export interface AmenitiesCategorized {
  incluido: string[];
  bajoPeticion?: string[];
  conCargo?: string[];
}

export interface Location {
  address: string;
  neighborhood: string;
  building?: string;
  googleMapsUrl?: string;
  coordinates: Coordinates;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface Capacity {
  guests: number;
  bedrooms: number;
  bathrooms: number;
  beds: number;
  sofaBeds: number;
  doubleBeds?: number;  // Camas dobles/matrimoniales
  singleBeds?: number;  // Camas sencillas/individuales
  nidoBeds?: number;    // Camas nido
}

// Datos de distribución de camas (soporta formato antiguo y nuevo)
export interface SleepSpaceData {
  descripcion: string;
  fotos?: string[]; // Rutas a fotos dedicadas "sleep-"
}

export interface DistribucionCamas {
  alcoba?: string | SleepSpaceData;
  sala?: string | SleepSpaceData;
  estudio?: string | SleepSpaceData;
  [key: string]: string | SleepSpaceData | undefined; // Permite cualquier clave adicional
}

export interface Pricing {
  basePrice: number;
  currency: string;
  minNights: number;
}

export interface Review {
  author: string;
  platform: 'Airbnb' | 'Booking.com' | 'Google' | 'WhatsApp';
  comment: string;
  date?: string; // Formato ISO: "2024-11-01"
  screenshotUrl?: string; // URL de la captura del comentario original
}

export interface SearchParams {
  location?: string;
  checkIn?: Date;
  checkOut?: Date;
  guests?: number;
}

/**
 * Item del feed TikTok-style
 * Representa un video con contexto mínimo del apartamento
 */
export interface VideoFeedItem {
  id: string;                    // ID único del item (aptId-videoIndex)
  apartmentId: string;
  apartmentName: string;         // publicName
  apartmentType: string;         // tipo (studio, alcoba, etc)
  neighborhood: string;          // location.neighborhood
  piso: number;
  video: ApartmentVideo;
  thumbnail: string;             // Thumbnail del apartamento
  // Datos para CTA
  enlaceWhatsApp?: string;
  airbnbUrl?: string;
  bookingUrl?: string;
}

/**
 * Genera el nombre completo para mostrar al público
 * Combina el tipo (Apartamento/Estudio) con el publicName
 * @example getDisplayName(apt) // "Apartamento Urban Sky" o "Estudio Sky Nest"
 * @example publicName "Apartamento vista Laguito - piso 7" // se devuelve tal cual (sin duplicar tipo)
 */
export function getDisplayName(apartment: Apartment): string {
  const prefix = apartment.tipo === 'studio' ? 'Estudio' : 'Apartamento';
  const name = apartment.publicName.trim();
  // Muchos publicName ya traen el tipo: anteponerlo otra vez duplicaba el
  // título en cards y aria-labels ("Apartamento Apartamento vista Laguito").
  if (/^(apartamento|estudio|apartaestudio)\b/i.test(name)) return name;
  return `${prefix} ${name}`;
}
